import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { UserDataProvider, useUserData } from './contexts/UserDataContext';
import { ThemeProvider } from './contexts/ThemeContext';
import Header from './components/Header';
import MarketTicker from './components/MarketTicker';
import TabNavigation from './components/TabNavigation';
import Login from './components/Login';
import Signup from './components/Signup';
import ProtectedRoute from './components/ProtectedRoute';
import Home from './pages/Home';
import HowItWorks from './pages/HowItWorks';
import Terms from './pages/Terms';
import Privacy from './pages/Privacy';
import NewsFeeds from './pages/NewsFeeds';
import SocialFeeds from './pages/SocialFeeds';
import Settings from './pages/Settings';
import BullPin from './pages/BullPin';
import BearDen from './pages/BearDen';
import SignupSuccess from './pages/SignupSuccess';
import SignupCancel from './pages/SignupCancel';
import analyticsService from './services/analyticsService';
import cacheService from './services/cacheService';
import CookieBanner from './components/CookieBanner';
import './App.css';

// Wrapper component to handle API key logic
const AppContent = () => {
  const { user } = useAuth();
  const { 
    userSettings, 
    userPrompts, 
    userFeeds, 
    getEffectiveApiKeys,
    loading: userDataLoading 
  } = useUserData();
  
  const [activeTab, setActiveTab] = useState('home');
  const [showLogin, setShowLogin] = useState(false);
  const [showSignup, setShowSignup] = useState(false);

  // Default managed OpenAI API key for MANAGED subscription users
  const defaultManagedKey = process.env.REACT_APP_OPENAI_API_KEY || '';

  // Get effective API keys based on user subscription
  const effectiveApiKeys = getEffectiveApiKeys();
  const effectiveApiKey = user?.subscriptionType === 'MANAGED' || user?.role === 'ADMIN' 
    ? defaultManagedKey 
    : effectiveApiKeys.openaiApiKey;

  // Initialize analytics and clean cache on app start
  useEffect(() => {
    // Initialize analytics
    analyticsService.initialize();
    
    // Clean up expired cache entries
    cacheService.cleanExpiredCache();
    
    // Listen for signup modal opening from login component
    const handleOpenSignupModal = () => {
      setShowSignup(true);
    };
    
    // Listen for login modal opening from signup success component
    const handleOpenLoginModal = () => {
      setShowLogin(true);
    };
    
    window.addEventListener('openSignupModal', handleOpenSignupModal);
    window.addEventListener('openLoginModal', handleOpenLoginModal);
    
    return () => {
      window.removeEventListener('openSignupModal', handleOpenSignupModal);
      window.removeEventListener('openLoginModal', handleOpenLoginModal);
    };
  }, []);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab === 'login') {
      setShowLogin(true);
    } else if (tab === 'signup') {
      setShowSignup(true);
    }
  };

  const handleCloseLogin = () => {
    setShowLogin(false);
    setShowSignup(false);
  };

  const handleLoginSuccess = () => {
    setShowLogin(false);
    setShowSignup(false);
    setActiveTab('news');
  };

  const handleSignupSuccess = () => {
    setShowLogin(false);
    setShowSignup(false);
    setActiveTab('news');
  };

  // Show loading while user data is being loaded
  if (userDataLoading) {
    console.log('App: Showing loading screen, userDataLoading:', userDataLoading);
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading your personalized dashboard...</p>
      </div>
    );
  }

  console.log('App: Rendering app, user:', user, 'userDataLoading:', userDataLoading);

  return (
    <div className="App">
      <CookieBanner />
      
      <Header 
        user={user} 
        onLoginClick={() => handleTabChange('login')}
        onSignupClick={() => handleTabChange('signup')}
      />
      
      <TabNavigation activeTab={activeTab} onTabChange={handleTabChange} />
      
      <MarketTicker userApiKeys={effectiveApiKeys} />
      
      <main className="main-content">
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/how-it-works" element={<HowItWorks />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/signup/success" element={<SignupSuccess />} />
          <Route path="/signup/cancel" element={<SignupCancel />} />
          
          <Route 
            path="/news" 
            element={
              <ProtectedRoute>
                 {(() => {
                   console.log('App: Passing to NewsFeeds - userFeeds:', userFeeds, 'userSettings:', userSettings, 'effectiveApiKeys:', effectiveApiKeys);
                   console.log('App: effectiveApiKey:', effectiveApiKey, 'user subscription:', user?.subscriptionType, 'user role:', user?.role);
                   return (
                     <NewsFeeds 
                       refreshInterval={userSettings?.refreshInterval || 300}
                       rssFeeds={userFeeds || []}
                       openaiApiKey={effectiveApiKey}
                       finnhubApiKey={effectiveApiKeys?.finnhubApiKey || process.env.REACT_APP_FINNHUB_API_KEY || ''}
                       finnhubEnabled={effectiveApiKeys?.finnhubEnabled !== false}
                       yahooFinanceEnabled={effectiveApiKeys?.yahooFinanceEnabled === true}
                       customPrompts={userPrompts || []}
                     />
                   );
                 })()}
              </ProtectedRoute>
            } 
          />
          
          <Route 
            path="/social" 
            element={
              <ProtectedRoute>
                <SocialFeeds 
                  userApiKeys={effectiveApiKeys || {}}
                  userSettings={userSettings}
                  openaiApiKey={effectiveApiKey}
                  customPrompts={userPrompts || []}
                />
              </ProtectedRoute>
            } 
          />
          
          <Route 
            path="/settings" 
            element={
              <ProtectedRoute>
                 <Settings 
                   refreshInterval={userSettings?.refreshInterval || 300}
                   rssFeeds={userFeeds || []}
                   userSettings={userSettings}
                   userPrompts={userPrompts || []}
                   userApiKeys={effectiveApiKeys || {}}
                   user={user}
                 />
              </ProtectedRoute>
            } 
          />
          
          <Route 
            path="/bull-pin" 
            element={
              <ProtectedRoute>
                <BullPin />
              </ProtectedRoute>
            } 
          />
          
          <Route 
            path="/bear-den" 
            element={
              <ProtectedRoute>
                <BearDen />
              </ProtectedRoute>
            } 
          />
        </Routes>
      </main>
      
      {showLogin && (
        <Login 
          onClose={handleCloseLogin}
          onLoginSuccess={handleLoginSuccess}
          onSignupClick={() => {
            setShowLogin(false);
            setShowSignup(true);
          }}
        />
      )}
      
      {showSignup && (
        <Signup 
          onClose={handleCloseLogin}
          onSignupSuccess={handleSignupSuccess}
          onLoginClick={() => {
            setShowSignup(false);
            setShowLogin(true);
          }}
        />
      )}
    </div>
  );
};

function App() {
  return (
    <Router>
      <ThemeProvider>
        <AuthProvider>
          <UserDataProvider>
            <AppContent />
          </UserDataProvider>
        </AuthProvider>
      </ThemeProvider>
    </Router>
  );
}

export default App;
